import { useEffect, useRef, useState } from 'react'
import type { Category } from '../types.ts'

type Props = {
  category?: Category
  categories: Category[]
  onClose: () => void
  onSave: (data: {
    name: string
    slug: string
    parent_id: number | null
  }) => void
  isSaving: boolean
}

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export function CategoryModal({ category, categories, onClose, onSave, isSaving }: Props) {
  const [nameVal, setNameVal] = useState(category?.name ?? '')
  const [slugVal, setSlugVal] = useState(category?.slug ?? '')
  const [parentIdVal, setParentIdVal] = useState<string>(
    category?.parent_id ? String(category.parent_id) : '',
  )
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const name = nameVal.trim()
    if (!name) return

    onSave({
      name,
      slug: (slugVal.trim() || slugify(name)),
      parent_id: parentIdVal ? Number(parentIdVal) : null,
    })
  }

  const parentOptions = categories.filter((c) => c.id !== category?.id)

  return (
    <div className="modal-overlay">
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{category ? 'Editar Categoria' : 'Nova Categoria'}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="field">
            <label htmlFor="category-name-input">Nome *</label>
            <input
              id="category-name-input"
              ref={inputRef}
              type="text"
              value={nameVal}
              onChange={(e) => setNameVal(e.target.value)}
              placeholder="Ex: RECEBÍVEIS"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="category-slug-input">Slug *</label>
            <input
              id="category-slug-input"
              type="text"
              value={slugVal}
              onChange={(e) => setSlugVal(e.target.value)}
              placeholder="Ex: recebiveis"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="category-parent-input">Categoria pai</label>
            <select
              id="category-parent-input"
              value={parentIdVal}
              onChange={(e) => setParentIdVal(e.target.value)}
            >
              <option value="">(raiz)</option>
              {parentOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary" disabled={isSaving}>
              {isSaving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
